import React, { createContext, useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { useNotifications } from '../../hooks/useNotifications';
import { authService } from '../../services/auth/authService';
import { tryResolveTenantSlug } from '../../tenancy/tenant.js';
import { useLanguage } from '../../i18n/LanguageContext.jsx';

const NotificationContext = createContext();

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext must be used within a NotificationsProvider');
  }
  return context;
};

export const NotificationsProvider = ({ children, disabled = false }) => {
  const notificationHook = useNotifications();
  const { activeLanguage } = useLanguage();
  const sessionIdentityRef = useRef(null);
  const activeLanguageRef = useRef(activeLanguage);
  const lifecycleActionsRef = useRef({
    cleanup: notificationHook.cleanup,
    initialize: notificationHook.initialize,
    reconnect: notificationHook.reconnect,
  });
  const synchronizeSessionRef = useRef(() => {});

  activeLanguageRef.current = activeLanguage;
  lifecycleActionsRef.current = {
    cleanup: notificationHook.cleanup,
    initialize: notificationHook.initialize,
    reconnect: notificationHook.reconnect,
  };

  // This provider is the sole owner of the notification connection lifecycle.
  useEffect(() => {
    if (disabled) {
      lifecycleActionsRef.current.cleanup();
      return undefined;
    }

    let disposed = false;

    const getSessionIdentity = () => {
      const tenant = tryResolveTenantSlug() || 'public';
      const token = authService.getToken();
      // Notification REST payloads and WebSocket tickets can both be localized.
      // Treat locale as part of the connection/session identity so a language
      // switch cannot keep an old-language stream alive.
      return {
        key: `${tenant}:${token || ''}:${activeLanguageRef.current || ''}`,
        token,
      };
    };

    const synchronizeSession = () => {
      if (disposed) return;
      const identity = getSessionIdentity();
      if (identity.key === sessionIdentityRef.current) return;

      sessionIdentityRef.current = identity.key;
      lifecycleActionsRef.current.cleanup();
      if (identity.token) {
        lifecycleActionsRef.current.initialize().catch(() => {
          // The service owns retries; the context reflects failures via its state.
        });
      }
    };

    synchronizeSessionRef.current = synchronizeSession;

    const reconnect = () => {
      if (!authService.isAuthenticated()) return;
      lifecycleActionsRef.current.reconnect().catch(() => {});
    };

    synchronizeSession();
    window.addEventListener('storage', synchronizeSession);
    window.addEventListener('atrux:auth-changed', synchronizeSession);
    window.addEventListener('atrux:notifications-reconnect', reconnect);
    window.addEventListener('popstate', synchronizeSession);
    document.addEventListener('visibilitychange', synchronizeSession);
    const sessionMonitor = setInterval(synchronizeSession, 1000);

    return () => {
      disposed = true;
      clearInterval(sessionMonitor);
      window.removeEventListener('storage', synchronizeSession);
      window.removeEventListener('atrux:auth-changed', synchronizeSession);
      window.removeEventListener('atrux:notifications-reconnect', reconnect);
      window.removeEventListener('popstate', synchronizeSession);
      document.removeEventListener('visibilitychange', synchronizeSession);
      synchronizeSessionRef.current = () => {};
      sessionIdentityRef.current = null;
      lifecycleActionsRef.current.cleanup();
    };
  }, [disabled]);

  // The lifecycle effect above remains mounted while this provider lives. A
  // locale change gets its own explicit synchronization so listeners/timers do
  // not churn and the old socket plus cached localized rows are invalidated
  // exactly once before the new-language fetch starts.
  useLayoutEffect(() => {
    if (disabled) return;
    synchronizeSessionRef.current();
  }, [activeLanguage, disabled]);

  const value = {
    ...notificationHook,
    // Add any additional methods or data you want to expose
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
